import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabase/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

export default function ClientSignUpForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First, check if the invite code is valid
      const { data: inviteData, error: inviteError } = await supabase
        .from("client_invitations")
        .select("organization_id, role")
        .eq("code", inviteCode)
        .eq("email", email)
        .single();

      if (inviteError) {
        throw new Error(
          "Invalid invitation code or email. Please check your details.",
        );
      }

      // Sign up with email and password
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (error) throw error;

      // Create user record in public.users table
      const { error: userError } = await supabase.from("users").insert([
        {
          id: data.user!.id,
          email: email,
          full_name: fullName,
        },
      ]);

      if (userError) throw userError;

      // Create organization_client_members record
      const { error: clientMemberError } = await supabase
        .from("organization_client_members")
        .insert([
          {
            user_id: data.user!.id,
            organization_id: inviteData.organization_id,
            role: inviteData.role,
          },
        ]);

      if (clientMemberError) throw clientMemberError;

      // Delete the used invitation
      await supabase
        .from("client_invitations")
        .delete()
        .eq("code", inviteCode)
        .eq("email", email);

      toast({
        title: "Account created",
        description:
          "Your account has been created successfully. You can now log in.",
      });

      navigate("/client/login");
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message || "An error occurred during sign up",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background text-foreground">
      <Card className="w-full max-w-md rounded-none border-border">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-foreground">
            Client Sign Up
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Create an account to access your client dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignUp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="bg-background border-border focus:border-primary focus:ring-0 text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-background border-border focus:border-primary focus:ring-0 text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-background border-border focus:border-primary focus:ring-0 text-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inviteCode">Invitation Code</Label>
              <Input
                id="inviteCode"
                placeholder="Enter your invitation code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                required
                className="bg-background border-border focus:border-primary focus:ring-0 text-foreground"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/80"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Sign Up"
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <div className="text-sm text-muted-foreground text-center w-full">
            Already have an account?{" "}
            <Button
              variant="link"
              className="p-0 h-auto font-normal text-primary hover:text-primary/80"
              onClick={() => navigate("/client/login")}
            >
              Log in
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
